import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { TOAST_ERROR_STYLE } from 'components/production/components/AisleContent/constants';

/**
 * Shared mutation lifecycle for aisle content editors.
 * Wraps TanStack Query's `useMutation` with toast notifications.
 *
 * @param {Object} options
 * @param {string} options.entityName - Display name for toast messages (e.g. 'Banner').
 * @param {Function} options.refetch - Callback invoked after every mutation.
 */
export function useEditorMutation({ entityName, refetch }) {
    const mutation = useMutation({
        mutationFn: ({ action }) => action(),
        onError: (err, { failure }) => {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error(failure ?? err.message, { style: TOAST_ERROR_STYLE });
        },
        onMutate: ({ loading }) => {
            const loadingToastID = toast.loading(loading ?? `Saving ${entityName}...`);
            return { loadingToastID };
        },
        onSettled: (_data, _error, _variables, context) => {
            toast.dismiss(context?.loadingToastID);
            refetch();
        },
        onSuccess: (_data, { success }) => {
            toast.success(success ?? `${entityName} saved successfully`);
        },
    });

    // Uses mutateAsync so callers can `await` and have it resolve only after
    // the mutation settles. The .catch() swallows the re-throw (errors are
    // already handled by onError) to avoid unhandled promise rejections.
    const executeMutation = (...args) => mutation.mutateAsync(...args).catch(() => {});

    return { executeMutation, isSaving: mutation.isPending };
}
